import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatDT } from "@/lib/format";
import {
  MessageCircle, Search, Filter, RefreshCw, DoorOpen, Calendar, Lock, CheckCircle2,
  Send, Loader2, ArrowLeft, User, Clock, Pencil, Trash2, ChevronDown, ChevronUp, ShieldCheck,
} from "lucide-react";

const EDIT_WINDOW_MINS = 10;

export default function GuestLog() {
  const { user, settings } = useAuth();
  const tz = settings?.general?.timezone || "Asia/Dhaka";
  const isReadOnly = user?.role !== "admin" && !!user?.permissions?.read_only;
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState("browse"); // "browse" | "thread"

  /* ---------------- Browse mode ---------------- */
  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [f, setF] = useState({ status: "OPEN", room: "", q: "", date_from: "", date_to: "" });
  const [roomSearch, setRoomSearch] = useState("");

  const loadThreads = useCallback(async () => {
    setLoadingThreads(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/guest-log/threads", { params });
      setThreads(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoadingThreads(false); }
  }, [f]);

  useEffect(() => { if (view === "browse") loadThreads(); }, [view, loadThreads]);

  /* ---------------- Thread (guest) mode ---------------- */
  const [bundle, setBundle] = useState(null); // { room, guest_name, checkin, checkout, thread, history }
  const [loadingThread, setLoadingThread] = useState(false);
  const [note, setNote] = useState("");
  const [posting, setPosting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 30000); return () => clearInterval(t); }, []);

  const openByRoom = async (room) => {
    if (!room) return;
    setLoadingThread(true); setView("thread");
    try {
      const { data } = await api.get("/guest-log/thread", { params: { room } });
      setBundle(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setView("browse"); }
    finally { setLoadingThread(false); }
  };

  const openByThreadId = async (id) => {
    setLoadingThread(true); setView("thread");
    try {
      const { data: t } = await api.get(`/guest-log/thread/${id}`);
      setBundle({
        room: t.room_no, guest_name: t.guest_name, checkin: t.checkin, checkout: t.checkout,
        thread: { id: t.id, status: t.status, closed_at: t.closed_at, closed_by: t.closed_by, entries: t.entries },
        history: [],
      });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setView("browse"); }
    finally { setLoadingThread(false); }
  };

  const refreshThread = () => {
    if (!bundle) return;
    if (bundle.thread?.id) openByThreadId(bundle.thread.id);
    else openByRoom(bundle.room);
  };

  // Arriving here from another page (e.g. "View Guest Log" on a glitch) with a
  // specific thread to jump straight into.
  useEffect(() => {
    const tid = location.state?.openThreadId;
    if (tid) {
      openByThreadId(tid);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const postEntry = async () => {
    const text = note.trim();
    if (!text || !bundle) return;
    setPosting(true);
    try {
      await api.post("/guest-log/entries", {
        thread_id: bundle.thread?.id || undefined,
        room_no: bundle.room, note: text,
      });
      setNote("");
      refreshThread();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPosting(false); }
  };

  const closeThread = async () => {
    if (!bundle?.thread?.id) return;
    if (!window.confirm("Close this guest log? Hosts won't be able to add more entries.")) return;
    setClosing(true);
    try { await api.post(`/guest-log/thread/${bundle.thread.id}/close`); toast.success("Log closed"); refreshThread(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setClosing(false); }
  };

  const reopenThread = async () => {
    if (!bundle?.thread?.id) return;
    try { await api.post(`/guest-log/thread/${bundle.thread.id}/reopen`); toast.success("Log reopened"); refreshThread(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const startEdit = (entry) => { setEditingId(entry.id); setEditText(entry.note); };
  const cancelEdit = () => { setEditingId(null); setEditText(""); };
  const saveEdit = async (id) => {
    const text = editText.trim();
    if (!text) return;
    setSavingEdit(true);
    try { await api.put(`/guest-log/entries/${id}`, { note: text }); setEditingId(null); refreshThread(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };
  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.delete(`/guest-log/entries/${id}`); refreshThread(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const canModify = (entry) => {
    if (isReadOnly) return false;
    if (!bundle?.thread) return false;
    if (bundle.thread.status !== "OPEN") return false;
    if (entry.logged_by !== user?.username) return false;
    const ageMins = (Date.now() - new Date(entry.created_at).getTime()) / 60000;
    return ageMins <= EDIT_WINDOW_MINS;
  };

  const fld = "w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm outline-none focus:border-[#4e73df]";

  /* ==================================================== */
  /* BROWSE MODE                                           */
  /* ==================================================== */
  if (view === "browse") {
    return (
      <div className="gm-fade-up">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2 text-[#3a3b45]">
            <div className="w-10 h-10 rounded-xl bg-[#4e73df]/10 flex items-center justify-center text-[#4e73df]"><MessageCircle className="w-5 h-5" /></div>
            <div>
              <h2 className="font-bold text-lg leading-none">Guest Interaction Log</h2>
              <p className="text-xs text-slate-400 mt-1">{threads.length} thread{threads.length !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <form
              className="flex items-center gap-1.5"
              onSubmit={(e) => { e.preventDefault(); openByRoom(roomSearch.trim()); }}
            >
              <input
                className={`${fld} w-40`} placeholder="Room number…" value={roomSearch}
                data-testid="guest-log-room-search" onChange={(e) => setRoomSearch(e.target.value)}
              />
              <button type="submit" data-testid="guest-log-search-btn" className="p-2.5 rounded-lg bg-[#4e73df] hover:bg-[#3a5fd0] text-white"><Search className="w-4 h-4" /></button>
            </form>
            <button data-testid="toggle-gl-filters" onClick={() => setShowFilters((s) => !s)} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50">
              <Filter className="w-4 h-4" /> Filters {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <button onClick={loadThreads} className="p-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-500"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>

        {showFilters && (
          <div className="bg-white rounded-2xl gm-shadow p-5 mb-5 gm-fade-up">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div><label className="text-xs font-semibold text-slate-500">Status</label>
                <select className={fld} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                  <option value="">All</option><option value="OPEN">Open</option><option value="CLOSED">Closed</option>
                </select></div>
              <div><label className="text-xs font-semibold text-slate-500">Room</label>
                <input className={fld} value={f.room} onChange={(e) => setF({ ...f, room: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Guest name</label>
                <input className={fld} value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">From</label>
                <input type="date" className={fld} value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">To</label>
                <input type="date" className={fld} value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></div>
              <div className="flex items-end"><button onClick={() => setF({ status: "OPEN", room: "", q: "", date_from: "", date_to: "" })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Reset</button></div>
            </div>
          </div>
        )}

        {loadingThreads ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#4e73df]" /></div>
        ) : threads.length === 0 ? (
          <div className="bg-white rounded-2xl gm-shadow py-20 text-center text-slate-400">
            <p className="font-medium">No guest log threads yet.</p>
            <p className="text-xs mt-1">Search a room number above to start one.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {threads.map((t) => (
              <button
                key={t.id} onClick={() => openByThreadId(t.id)} data-testid={`gl-thread-${t.room_no}`}
                className="text-left bg-white rounded-2xl gm-shadow p-5 flex flex-col gap-2 hover:-translate-y-0.5 transition-transform"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.status === "OPEN" ? "bg-[#4e73df]/10 text-[#4e73df]" : "bg-slate-100 text-slate-400"}`}><DoorOpen className="w-4 h-4" /></span>
                    <span className="font-bold text-[#3a3b45]">Room {t.room_no || "-"}</span>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${t.status === "OPEN" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>{t.status}</span>
                </div>
                <div className="text-xs text-slate-500 break-words">{t.guest_name || "No guest on file"}</div>
                {(t.checkin || t.checkout) && (
                  <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" /> {t.checkin || "?"} – {t.checkout || "?"}
                  </div>
                )}
                <div className="flex items-center justify-between text-[11px] text-slate-400 pt-3 mt-1 border-t border-slate-100">
                  <span className="flex items-center gap-1">
                    {t.status === "CLOSED" ? <Lock className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    {t.status === "CLOSED" ? `Closed ${t.closed_by === "auto" ? "automatically" : `by ${t.closed_by}`}` : "In progress"}
                  </span>
                  <span>{t.entry_count} entr{t.entry_count === 1 ? "y" : "ies"}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ==================================================== */
  /* THREAD (GUEST) MODE                                   */
  /* ==================================================== */
  const thread = bundle?.thread;
  const isOpen = thread?.status === "OPEN";
  const isVacant = !!bundle && !thread && !bundle.opera_found;

  return (
    <div className="gm-fade-up">
      <button
        onClick={() => setView("browse")} data-testid="gl-back-to-browse"
        className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#4e73df] mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Guest Log
      </button>

      {loadingThread || !bundle ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#4e73df]" /></div>
      ) : (
        <div className="space-y-4">
          {/* GUEST HEADER */}
          <div className="bg-white rounded-2xl gm-shadow p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isVacant ? "bg-slate-100 text-slate-400" : "bg-[#4e73df]/10 text-[#4e73df]"}`}><DoorOpen className="w-5 h-5" /></div>
              <div className="min-w-0">
                <div className="font-bold text-lg text-[#3a3b45] leading-none">Room {bundle.room || "-"}</div>
                <div className="text-sm text-slate-500 mt-1 break-words">{isVacant ? "No guest currently checked in" : (bundle.guest_name || "No guest on file")}</div>
                {(bundle.checkin || bundle.checkout) && (
                  <div className="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" /> {bundle.checkin || "?"} – {bundle.checkout || "?"}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {isVacant && <span className="text-[11px] font-semibold px-3 py-1 rounded-full bg-slate-100 text-slate-500">VACANT</span>}
              {thread && (
                <>
                  <span className={`text-[11px] font-semibold px-3 py-1 rounded-full ${isOpen ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>{thread.status}</span>
                  {isReadOnly ? null : isOpen ? (
                    <button onClick={closeThread} disabled={closing} data-testid="gl-close-thread" className="px-3.5 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-60">
                      {closing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Close Log
                    </button>
                  ) : user?.role === "admin" ? (
                    <button onClick={reopenThread} data-testid="gl-reopen-thread" className="px-3.5 py-2 rounded-lg border border-[#4e73df] text-[#4e73df] text-sm font-semibold flex items-center gap-1.5 hover:bg-[#4e73df]/5">
                      <ShieldCheck className="w-4 h-4" /> Reopen
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </div>

          {isVacant ? (
            <div className="bg-white rounded-2xl gm-shadow py-14 text-center text-slate-400">
              <DoorOpen className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="font-medium text-slate-500">Vacant room</p>
              <p className="text-xs mt-1">No guest is currently checked in — nothing to log yet.</p>
            </div>
          ) : (
          <>
          {/* COMPOSER or CLOSED BANNER */}
          {isReadOnly ? (
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-2.5 text-sm text-slate-600">
              <Lock className="w-4 h-4 shrink-0 text-slate-400" /> Your account has view-only access to Guest Log.
            </div>
          ) : !thread || isOpen ? (
            <div className="bg-white rounded-2xl gm-shadow p-4 space-y-3">
              <textarea
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm outline-none focus:border-[#4e73df] focus:ring-2 focus:ring-[#4e73df]/20"
                rows={2} placeholder="What did you discuss with the guest?" value={note}
                data-testid="gl-note-input" onChange={(e) => setNote(e.target.value)}
              />
              <div className="flex justify-end">
                <button onClick={postEntry} disabled={posting || !note.trim()} data-testid="gl-post-btn"
                  className="px-5 py-2.5 rounded-lg bg-[#4e73df] hover:bg-[#3a5fd0] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
                  {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-2.5 text-sm text-slate-600">
              <Lock className="w-4 h-4 shrink-0 text-slate-400" />
              This log {thread.closed_by === "auto" ? "closed automatically" : `was closed by ${thread.closed_by}`} on {formatDT(thread.closed_at, tz)} — read only.
            </div>
          )}

          {/* FEED */}
          <div className="space-y-2.5">
            {(thread?.entries || []).length === 0 ? (
              <div className="bg-white rounded-2xl gm-shadow py-14 text-center text-slate-400">
                <p className="text-sm">No entries yet — the first note posted here starts the log.</p>
              </div>
            ) : (
              [...thread.entries].reverse().map((e) => (
                <div key={e.id} data-testid={`gl-entry-${e.id}`} className={`bg-white rounded-xl border p-4 flex gap-3 ${canModify(e) ? "border-[#dbeafe]" : "border-slate-100"}`}>
                  <span className="w-8 h-8 shrink-0 rounded-full bg-[#4e73df] text-white text-[11px] font-bold flex items-center justify-center">
                    {(e.logged_by || "?").slice(0, 2).toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-semibold text-sm text-[#3a3b45] truncate">{e.logged_by}</span>
                        {canModify(e) && editingId !== e.id && (
                          <button onClick={() => startEdit(e)} data-testid={`gl-edit-${e.id}`} className="text-[10px] font-semibold text-[#4e73df] bg-[#4e73df]/10 px-2 py-0.5 rounded-full flex items-center gap-1 shrink-0">
                            <Pencil className="w-3 h-3" /> Edit
                          </button>
                        )}
                        {canModify(e) && editingId !== e.id && (
                          <button onClick={() => deleteEntry(e.id)} data-testid={`gl-delete-${e.id}`} className="text-[10px] font-semibold text-red-500 bg-red-50 px-2 py-0.5 rounded-full flex items-center gap-1 shrink-0">
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        )}
                      </div>
                      <span className="text-[11px] text-slate-400 shrink-0">{formatDT(e.created_at, tz)}</span>
                    </div>
                    {editingId === e.id ? (
                      <div className="mt-2 space-y-2">
                        <textarea
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm outline-none focus:border-[#4e73df]"
                          rows={2} value={editText} onChange={(e2) => setEditText(e2.target.value)}
                        />
                        <div className="flex gap-2 justify-end">
                          <button onClick={cancelEdit} className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600">Cancel</button>
                          <button onClick={() => saveEdit(e.id)} disabled={savingEdit} className="px-3 py-1.5 rounded-lg bg-[#4e73df] text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60">
                            {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-600 mt-1.5 whitespace-pre-wrap break-words">{e.note}{e.edited_at ? <span className="text-[10px] text-slate-300 italic"> (edited)</span> : null}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          </>
          )}

          {/* HISTORY (older stays for the same room) */}
          {bundle.history && bundle.history.length > 0 && (
            <div className="pt-2">
              <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Past logs for this room</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {bundle.history.map((h) => (
                  <button key={h.id} onClick={() => openByThreadId(h.id)} className="text-left px-4 py-3 rounded-lg border border-slate-100 bg-slate-50/50 hover:bg-slate-50 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{h.guest_name || "No guest on file"}</p>
                      <p className="text-xs text-slate-400">{h.checkin || "?"} – {h.checkout || "?"} · {h.entry_count} entries</p>
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${h.status === "OPEN" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>{h.status}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
